/**
 * Store reducer. Pure — one call per action. Snapshots do NOT flow
 * through here at 30 Hz; the sampled `hud.sample` fires at 10 Hz.
 *
 * Rejection timeouts and cast-flash timeouts are handled by dropping
 * old entries in the reducer at each `hud.tick` — the controller
 * dispatches ticks at the HUD sample rate.
 */

import type { Action, CastErrorEntry, RejectionEntry, StoreState, KeypressFlash } from "./types.js";
import { INITIAL_STATE } from "./types.js";

/** Rejection toast lifetime (ms). */
export const REJECTION_TTL_MS = 3000;

/** Cast-flash duration (ms) — matches the wp7 spec's 150ms hint. */
export const CAST_FLASH_TTL_MS = 150;

let rejectionIdSeq = 1;

/** Pure reducer. */
export function reducer(state: StoreState, action: Action): StoreState {
  switch (action.type) {
    case "connection.status": {
      return { ...state, connection: action.next };
    }
    case "match.created": {
      return {
        ...state,
        matchId: action.matchId,
        joinUrl: action.joinUrl,
        joinError: null,
        serverError: null,
        phase: { phase: "waiting", winner: null, endsAtTick: null },
        mapSlug: action.mapSlug,
      };
    }
    case "match.joined": {
      return {
        ...state,
        matchId: action.matchId,
        ownSeat: action.ownSeat,
        joinError: null,
      };
    }
    case "match.phase": {
      return {
        ...state,
        phase: { phase: action.phase, winner: action.winner, endsAtTick: action.endsAtTick },
        roster: action.roster,
        damageDealt: action.damageDealt,
        mapSlug: action.mapSlug,
      };
    }
    case "match.leave": {
      return {
        ...INITIAL_STATE,
        connection: state.connection,
      };
    }
    case "match.champion": {
      return { ...state, ownChampionId: action.championId, ownLoadout: action.loadout };
    }
    case "hud.sample": {
      return { ...state, hudSnapshot: action.snapshot };
    }
    case "hud.tick": {
      const now = Date.now();
      const rejections = pruneRejections(state.rejections, now);
      const flashes = pruneFlashes(state.castFlashes, now);
      if (rejections === state.rejections && flashes === state.castFlashes) {
        return { ...state, hudTick: state.hudTick + 1 };
      }
      return {
        ...state,
        hudTick: state.hudTick + 1,
        rejections,
        castFlashes: flashes,
      };
    }
    case "rejection.add": {
      const entry: RejectionEntry = {
        id: rejectionIdSeq++,
        reason: action.reason,
        ts: action.ts,
      };
      return { ...state, rejections: [...state.rejections, entry] };
    }
    case "rejection.drop": {
      return {
        ...state,
        rejections: state.rejections.filter((r) => r.id !== action.id),
      };
    }
    case "castFlash.add": {
      const flash: KeypressFlash = { slot: action.slot, ts: action.ts };
      return { ...state, castFlashes: [...state.castFlashes, flash] };
    }
    case "castFlash.drop": {
      return {
        ...state,
        castFlashes: state.castFlashes.filter((f) => f.ts !== action.ts),
      };
    }
    case "castError.add": {
      // #787: COALESCE by reason — mashing an unaffordable key must show ONE
      // message, not N. An existing entry for this reason has its `ts` (and
      // payload, e.g. a different pool) refreshed in place, which also restarts
      // the notice's dismissal timer; a new reason appends.
      //
      // The two reason comparisons here are lint-suppressed because
      // `CastBlockReason` has exactly ONE member today (`insufficientResource`),
      // so TS can prove them constant. They are NOT dead: out-of-range (#352),
      // school-lock and invalid-target are the intended next arms (see
      // `input/castability.ts`), and coalescing must key off the reason the
      // moment a second one exists. Deleting them would be a latent bug.
      const existing = state.castErrors.findIndex(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- one-arm union today; see above
        (e) => e.block.reason === action.block.reason,
      );
      const entry: CastErrorEntry = { block: action.block, ts: action.ts };
      if (existing === -1) {
        return { ...state, castErrors: [...state.castErrors, entry] };
      }
      const next = state.castErrors.slice();
      next[existing] = entry;
      return { ...state, castErrors: next };
    }
    case "castError.drop": {
      return {
        ...state,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- one-arm union today; see `castError.add`
        castErrors: state.castErrors.filter((e) => e.block.reason !== action.reason),
      };
    }
    case "joinError.set": {
      return { ...state, joinError: action.reason };
    }
    case "serverError": {
      return {
        ...state,
        serverError: { code: action.code, ts: action.ts },
      };
    }
    case "serverError.clear": {
      return { ...state, serverError: null };
    }
    case "practice.botList": {
      return { ...state, practiceBots: action.bots };
    }
    case "chat.setOpen": {
      // #833: identity-return when already in the requested state so a
      // redundant close (e.g. Escape on an already-closed box) doesn't
      // re-render every store consumer.
      if (state.chatOpen === action.open) return state;
      return { ...state, chatOpen: action.open };
    }
  }
}

function pruneRejections(
  entries: readonly RejectionEntry[],
  now: number,
): readonly RejectionEntry[] {
  const kept = entries.filter((e) => now - e.ts < REJECTION_TTL_MS);
  return kept.length === entries.length ? entries : kept;
}

function pruneFlashes(entries: readonly KeypressFlash[], now: number): readonly KeypressFlash[] {
  const kept = entries.filter((e) => now - e.ts < CAST_FLASH_TTL_MS);
  return kept.length === entries.length ? entries : kept;
}
